import { FiPlus } from "react-icons/fi";
import { useState, useEffect, useCallback, useRef } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Menu, MenuButton, MenuItem, MenuItems, Switch } from "@headlessui/react";
import { FaEquals, FaFolder } from "react-icons/fa6";
import { TbCardsFilled } from "react-icons/tb";
import { IoTrashOutline } from "react-icons/io5";
import { ImSpinner8 } from "react-icons/im";
import CreateSubjectmodel from "../model/CreateSubjectmodel";
import WhoSee from "../model/WhoSee";
import MaterialDeckDetailModel from "../model/MaterialDeckDetailModel.jsx";
import { RxCross2 } from "react-icons/rx";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { useDispatch } from "react-redux";
import { setCoursePrivacydata } from "../../redux/slices/coursePrivacySlice.jsx";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ApiGet, ApiDelete, ApiPost, ApiPut } from "../../helper/axiosFunctions";
import { commonToast } from "../../helper/Toast";
import MaterialSkeleton from "../skeleton/MaterialSkeleton";
import { RiPencilFill } from "react-icons/ri";
import { removeAlertPopup } from "../../helper/commonFunctions.jsx";

const useAutoScroll = () => {
  const scrollIntervalRef = useRef(null);
  const lastScrollTime = useRef(0);
  const scrollSpeed = 10; // Reduced base speed for smoother scrolling
  const minScrollInterval = 16; // ~60fps for smooth scrolling
  const [scrollThreshold, setScrollThreshold] = useState(window.innerHeight * 0.15);

  // Update threshold on window resize
  useEffect(() => {
    const updateThreshold = () => {
      setScrollThreshold(window.innerHeight * 0.15);
    };

    updateThreshold();
    window.addEventListener('resize', updateThreshold);
    return () => {
      window.removeEventListener('resize', updateThreshold);
      stopScrolling(); // Ensure cleanup
    };
  }, []);

  const startScrolling = useCallback((direction, factor = 1) => {
    if (scrollIntervalRef.current) return;

    const scroll = () => {
      const now = Date.now();
      if (now - lastScrollTime.current >= minScrollInterval) {
        const scrollAmount = direction * scrollSpeed * factor;
        window.scrollBy({
          top: scrollAmount,
          behavior: 'auto'
        });
        lastScrollTime.current = now;
      }
      scrollIntervalRef.current = requestAnimationFrame(scroll);
    };

    scroll();
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    const mouseY = e.clientY;
    const windowHeight = window.innerHeight;
    const threshold = scrollThreshold;

    if (mouseY < threshold) {
      // Top zone - scroll up
      const factor = Math.max(0.1, 1 - (mouseY / threshold));
      startScrolling(-1, factor);
    } else if (mouseY > windowHeight - threshold) {
      // Bottom zone - scroll down
      const factor = Math.max(0.1, (mouseY - (windowHeight - threshold)) / threshold);
      startScrolling(1, factor);
    } else {
      stopScrolling();
    }
  }, [startScrolling, stopScrolling, scrollThreshold]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScrolling();
    };
  }, [stopScrolling]);

  return { handleDragOver, stopScrolling };
};

export default function Material() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  // eslint-disable-next-line 
  const [enabled, setEnabled] = useState(false);
  const [click, setClick] = useState(false);
  const [isCreatemodel, setIsCreatemodel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [decks, setDecks] = useState([]);
  const [deletingDeckId, setDeletingDeckId] = useState(null);
  const [activities, setActivities] = useState([])
  const [deletingActivityDeckId, setDeletingActivityDeckId] = useState(null);
  const [draggingDeck, setDraggingDeck] = useState(null);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editedActivityName, setEditedActivityName] = useState("");
  const [movingDeck, setMovingDeck] = useState(null);
  const [targetActivity, setTargetActivity] = useState(null);
  const [hide, sethide] = useState(() => {
    const savedHide = localStorage.getItem('material_helper_hidden');
    return savedHide ? JSON.parse(savedHide) : false;
  });
  const [selectedDeckData, setSelectedDeckData] = useState({})
  const { handleDragOver, stopScrolling } = useAutoScroll();

  const toggleHide = () => {
    const newHideState = !hide;
    sethide(newHideState);
    localStorage.setItem('material_helper_hidden', JSON.stringify(newHideState));
  };

  const classId = pathname?.split('/')[2];
  const teacherid = JSON.parse(localStorage?.getItem("zubrus_user_data"))?.data?.userInfo?.userId;

  const fetchDecks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiGet(`/contents/classes/${classId}/decks`);
      setDecks(response?.data || []);
    } catch (error) {
      commonToast(error?.data?.error || error?.message, "error");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  const fetchactivities = async () => {
    try {
      const response = await ApiGet(`/activities/teachers/${teacherid}`)
      setActivities(response?.data || [])
    } catch (error) {
      commonToast(error?.data?.error || error?.message, "error");
    }
  }

  useEffect(() => {
    fetchDecks();
    // fetchactivities();
  }, [fetchDecks]);

  const handleDeleteDeck = (deckId) => {
    setDeletingDeckId(deckId);
    removeAlertPopup({
      t,
      preConfirm: async () => {
        try {
          await ApiDelete(`/contents/classes/${classId}/decks/${deckId}`);
          commonToast(t("deckremove"), "success");
          fetchDecks();
        } catch (error) {
          commonToast(error?.data?.errorDescription || error?.data?.error, "error");
        }
      }
    })
    setDeletingDeckId(null);
  };

  const handleDeleteActivityDeck = (activityId, deckId) => {
    setDeletingActivityDeckId(deckId);
    removeAlertPopup({
      t,
      preConfirm: async () => {
        try {
          await ApiDelete(`/activities/${activityId}/decks/${deckId}`);
          commonToast(t("deckremove"), "success");
          fetchactivities();
        } catch (error) {
          commonToast(error?.data?.errorDescription || error?.data?.error, "error");
        }
      }
    })
    setDeletingActivityDeckId(null);
  };

  const handleDropDeck = async (activityId, deck) => {
    setMovingDeck(deck.deckId);
    setTargetActivity(activityId);
    try {
      await ApiPost(`/activities/${activityId}/decks`, {
        deckId: deck.deckId,
        deckName: deck.deckName,
        sortOrder: 1,
        timeLimit: 60
      });
      commonToast(t("deck_added_successfully"), "success");
      fetchactivities();
    } catch (error) {
      commonToast(error?.data?.errorDescription || error?.data?.error, "error");
    } finally {
      setMovingDeck(null);
      setTargetActivity(null);
    }
  };

  const handleMoveDeck = async (fromActivityId, toActivityId, deck) => {
    setMovingDeck(deck.deckId);
    setTargetActivity(toActivityId);
    try {
      await ApiDelete(`/activities/${fromActivityId}/decks/${deck.deckId}`);
      await ApiPost(`/activities/${toActivityId}/decks`, {
        deckId: deck.deckId,
        deckName: deck.deckName,
        sortOrder: 1,
        timeLimit: 60
      });
      commonToast(t("deck_moved_successfully"), "success");
      fetchactivities();
    } catch (error) {
      commonToast(error?.data?.errorDescription || error?.data?.error, "error");
    } finally {
      setMovingDeck(null);
      setTargetActivity(null);
    }
  };

  const handleCreateActivity = async () => {
    try {
      setLoading(true);
      await ApiPost('/activities', {
        name: "test",
        description: "test",
        decks: [], // Empty array for decks
        activityType: "TEST",
        progressStatus: "NOT_STARTED"
      });
      commonToast(t("activity_created_successfully"), "success");
      fetchactivities();
    } catch (error) {
      commonToast(error?.data?.errorDescription || error?.data?.error, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateActivityName = async (activityId) => {
    try {
      setLoading(true);
      await ApiPut(`/activities`, {
        activityId: activityId,
        name: editedActivityName
      });
      commonToast(t("activity_updated_successfully"), "success");
      fetchactivities();
      setEditingActivityId(null);
    } catch (error) {
      commonToast(error?.data?.errorDescription || error?.data?.error, "error");
    } finally {
      setLoading(false);
    }
  };

  const ActivityDropZone = ({ activity, children }) => {
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: "DECK",
      drop: (item, monitor) => {
        stopScrolling();
        const didDrop = monitor.didDrop();
        if (didDrop) return;

        if (item.activityId && item.activityId !== activity.activityId) {
          handleMoveDeck(item.activityId, activity.activityId, item);
        } else {
          handleDropDeck(activity.activityId, item);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    const isMovingToThisActivity = targetActivity === activity.activityId;

    return (
      <div
        ref={drop}
        className={`relative transition-all duration-200 ${isMovingToThisActivity ? "bg-blue-50" : ""
          } ${isOver && canDrop ? "bg-blue-50 transform scale-[1.02]" : ""
          }`}
      >
        {isMovingToThisActivity && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-50">
            <ImSpinner8 className="text-blue-500 text-2xl animate-spin" />
          </div>
        )}
        {children}
      </div>
    );
  };

  const DraggableDeck = ({ deck, activityId }) => {
    const [{ isDragging }, drag] = useDrag({
      type: "DECK",
      item: { ...deck, activityId },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {
        stopScrolling();
      },
    });

    const isMoving = movingDeck === deck.deckId;

    return (
      <div
        ref={drag}
        data-deck-id={deck.deckId}
        className={`task relative flex sm:flex-col items-center gap-5 my-5 first:mt-0 last:mb-0 transition-all duration-200 ${isDragging ? "opacity-50 transform scale-95" : isMoving ? "opacity-50" : "hover:scale-[1.02]"
          }`}
      >
        <div
          className={`flex relative items-center justify-between md:justify-center gap-5 bg-white w-full rounded-3xl transition-all duration-200 ${isDragging ? "hover:bg-blue-50" : "hover:bg-gray-50"
            }`}
          onClick={() => {
            setSelectedDeckData(deck);
            dispatch(setCoursePrivacydata("Nematoniekas"))
          }}
        >
          {isMoving && (
            <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-50 rounded-3xl">
              <ImSpinner8 className="text-blue-500 text-2xl animate-spin" />
            </div>
          )}
          <div
            className="absolute cursor-pointer right-3 top-3 group"
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteDeck(deck.deckId);
            }}
          >
            {deletingDeckId === deck.deckId ? (
              <div className="p-2">
                <ImSpinner8 className="text-red-500 text-xl animate-spin" />
              </div>
            ) : (
              <div className="p-2 rounded-full group-hover:bg-red-50 transition-colors duration-200">
                <IoTrashOutline className="text-red-500 text-xl group-hover:scale- transition-transform duration-200" />
              </div>
            )}
          </div>
          <div className="flex sm:flex-col items-center gap-5 w-full p-5">
            {enabled && <FaEquals className="lg:absolute lg:left-3 lg:top-3" />}
            <img src={deck?.smallImageUrl} loading='lazy' alt={deck?.deckName} className='w-[120px] h-[88px] object-cover rounded-2xl' />
            <div>
              <h2 className="text-lg font-bold md:text-center">{deck?.deckName}</h2>
              <div className="flex sm:flex-col items-center md:items-start gap-5 underline regular-text mt-2">
                <h3 className="sm:items-center sm:w-full sm:flex justify-center">{t("questions")} ({deck?.cards?.length || 0})</h3>
                <h3 className="sm:items-center sm:w-full sm:flex justify-center">{t("Statistics")}</h3>
                <h3 className="sm:items-center sm:w-full sm:flex justify-center">{t("about")}</h3>
              </div>
            </div>
          </div>
          {enabled && (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteActivityDeck(activityId, deck.deckId);
                }}
                className="p-2 hover:bg-red-50 rounded-full transition-colors duration-200"
              >
                {deletingActivityDeckId === deck.deckId ? (
                  <ImSpinner8 className="text-red-500 text-xl animate-spin" />
                ) : (
                  <IoTrashOutline className="text-red-500 text-xl" />
                )}
              </button>
              <RxCross2
                color="black"
                className="lg:absolute lg:right-3 lg:top-3"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const Task = ({ task, index, moveTask, currentTab }) => {
    const [{ isDragging }, dragRef] = useDrag({
      type: "TASK",
      item: { index, currentTab },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [, dropRef] = useDrop({
      accept: "TASK",
      hover: (draggedItem) => {
        if (
          draggedItem.index !== index ||
          draggedItem.currentTab !== currentTab
        ) {
          moveTask(
            draggedItem.currentTab,
            currentTab,
            draggedItem.index,
            index
          );
          draggedItem.index = index;
          draggedItem.currentTab = currentTab;
        }
      },
    });

    return (
      <div
        ref={(node) => dragRef(dropRef(node))}
        className="task relative flex sm:flex-col items-center gap-5 my-5 first:mt-0 last:mb-0"
        style={{
          opacity: isDragging ? 0.5 : 1,
          backgroundColor: isDragging ? "green" : 'white'
        }}
      >
        <div
          className="flex relative cursor-pointer items-center justify-between md:justify-center gap-5 bg-white w-full rounded-3xl hover:bg-gray-50 transition-colors duration-200"
          onClick={() => dispatch(setCoursePrivacydata("Nematoniekas"))}
        >
          <div
            className="absolute right-3 top-3 group"
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteDeck(task.deckId);
            }}
          >
            {deletingDeckId === task.deckId ? (
              <div className="p-2">
                <ImSpinner8 className="text-red-500 text-xl animate-spin" />
              </div>
            ) : (
              <div className="p-2 rounded-full group-hover:bg-red-50 transition-colors duration-200">
                <IoTrashOutline className="text-red-500 text-xl group-hover:scale-110 transition-transform duration-200" />
              </div>
            )}
          </div>
          <div className="flex sm:flex-col items-center gap-5 w-full p-5">
            {enabled && <FaEquals className="lg:absolute lg:left-3 lg:top-3" />}
            <img src={task?.smallImageUrl} loading='lazy' alt={task?.deckName} className='w-[120px] h-[88px] object-cover rounded-2xl ' />
            <div>
              <h2 className="text-lg font-bold md:text-center">{task?.deckName}</h2>
              <div className="flex sm:flex-col items-center md:items-start gap-5 underline regular-text mt-2">
                <h3 className="sm:items-center sm:w-full sm:flex justify-center">{t("questions")} ({task?.cards?.length || 0})</h3>
                <h3 className="sm:items-center sm:w-full sm:flex justify-center">{t("Statistics")}</h3>
                <h3 className="sm:items-center sm:w-full sm:flex justify-center">{t("about")}</h3>
              </div>
            </div>
          </div>
          {enabled && (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDeck(task._id);
                }}
                className="p-2 hover:bg-red-50 rounded-full transition-colors duration-200"
              >
                <IoTrashOutline className="text-red-500 text-xl" />
              </button>
              <RxCross2
                color="black"
                className="lg:absolute lg:right-3 lg:top-3"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const NoDataMessage = () => (
    <div className="text-center py-8">
      <p className="text-lg text-gray-500">{t("no_decks_found")}</p>
    </div>
  );


  return (
    <DndProvider backend={HTML5Backend}>
      <div
        className={`flex lg:flex-col  ${hide ? 'justify-start' : 'justify-center'}  items-start gap-10`}
        onDragOver={(e) => {
          e.preventDefault();
          handleDragOver(e);
        }}
      >
        <div className={`max-w-5xl ${hide ? 'w-full' : 'w-[880px]'} lg:w-full`}>
          <div className={`bg-white p-5 ${enabled ? "rounded-t-3xl" : "rounded-3xl"}`}>
            <div className="flex justify-end items-center">
              {/* <div className="flex items-center gap-3">
              <Switch
                checked={enabled}
                onChange={setEnabled}
                className="group relative flex h-7 w-14 cursor-pointer rounded-full bg-lightgray p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[checked]:bg-darkblue"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-white ring-0 shadow-lg transition duration-200 ease-in-out group-data-[checked]:translate-x-7"
                />
              </Switch>
              <span className="regular-text">{t("edit")}</span>
            </div> */}

              <div className="flex items-center gap-3">
                <Menu>
                  <MenuButton
                    onClick={() => setClick(!click)}
                    className="inline-flex items-center gap-2 rounded-md py-1.5 text-sm/6 font-semibold focus:outline-none data-[open]:rounded-full data-[focus]:outline-1"
                  >
                    <span className="regular-text">{t("add")}</span>
                    <FiPlus
                      fontSize={20}
                      className={`${click && "bg-darkblue text-white"
                        } border rounded-full size-8 p-1 flex justify-center items-center`}
                    />
                  </MenuButton>

                  <MenuItems
                    transition
                    anchor="bottom end"
                    className="w-52 origin-top-right rounded-xl border border-white/5 bg-white shadow-md p-1 text-sm/6 text-black transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[hover]:bg-lightgray data-[closed]:opacity-0"
                  >
                    <MenuItem>
                      <button
                        onClick={() => {
                          setClick(false);
                          setIsCreatemodel(true);
                        }}
                        className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-[#F7F7F7]"
                      >
                        <TbCardsFilled className="size-5" />
                        {t("castle")}
                      </button>
                    </MenuItem>
                    {/* <MenuItem>
                    <button
                      onClick={() => {
                        setClick(false);
                          handleCreateActivity();
                      }}
                      className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-[#F7F7F7]"
                    >
                        <FaFolder className="size-4" />
                      {t("folder")}
                    </button>
                  </MenuItem> */}
                    <div className="my-1 h-px bg-white/5" />
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>
          {enabled && (
            <div className="bg-darkblue rounded-b-3xl text-white px-5 py-1.5 text-sm font-normal">
              <span className="font-semibold"> {t("editmode")}</span> - {t("adjust")}
            </div>
          )}

          <div className="min-h-screen pb-20">
            {activities && activities?.map((item) => (
              <ActivityDropZone key={item.activityId} activity={item}>
                <div className="w-full bg-white my-5 p-5 rounded-3xl">
                  <div className="font-bold flex items-center justify-between">
                    {editingActivityId === item.activityId ? (
                      <div className="flex items-center gap-2 w-full">
                        <input
                          type="text"
                          value={editedActivityName}
                          onChange={(e) => {
                            e.stopPropagation();
                            setEditedActivityName(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateActivityName(item.activityId);
                            } else if (e.key === 'Escape') {
                              setEditingActivityId(null);
                              setEditedActivityName("");
                            }
                          }}
                          className="flex-1 px-3 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={t("enter_activity_name")}
                          autoFocus
                        />
                        <button
                          onClick={() => handleUpdateActivityName(item.activityId)}
                          className="px-4 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          {t("save")}
                        </button>
                        <button
                          onClick={() => {
                            setEditingActivityId(null);
                            setEditedActivityName("");
                          }}
                          className="px-4 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    ) : (
                      <>
                        <span>{item.name}</span>
                        <div className="flex items-center gap-2">
                          {enabled && (
                            <button
                              onClick={() => {
                                setEditingActivityId(item.activityId);
                                setEditedActivityName(item.name);
                              }}
                              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            >
                              <RiPencilFill className="text-gray-500" />
                            </button>
                          )}
                          {item?.decks?.length === 0 && (
                            <div className="text-sm text-gray-500">
                              {t("empty_activity")}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {item?.decks?.map((deck) => (
                    <DraggableDeck key={deck.deckId} deck={deck} activityId={item.activityId} />
                  ))}
                </div>
              </ActivityDropZone>
            ))}

            <div className="app">
              {loading ? (
                <MaterialSkeleton />
              ) : decks?.length > 0 ? (
                <div className="space-y-5 mt-5">
                  {decks.map((deck, index) => (
                    <DraggableDeck key={deck._id} deck={deck} activityId={deck.activityId} />
                  ))}
                </div>
              ) : (
                // <NoDataMessage />
                null
              )}
            </div>
          </div>
        </div>

        <div className={`${hide ? "hidden" : 'block'} md:w-full bg-white rounded-3xl p-5 relative w-[316px] lg:w-full`}>
          <img
            src="/assets/images/Helper.png"
            alt="helper"
            className="absolute -top-5 right-0"
          />
          <h2 className="text-lg font-bold">{t("whatmaterial")}</h2>

          <div className="flex items-center justify-between mt-5">
            <div>
              <h4 className="regular-text">
                {t("questions")} {t("deck")}
              </h4>
              <p className="base-text text-lightgray">{t("mqa2")}</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-5">
            <div>
              <h4 className="regular-text">{t("folder")}</h4>
              <p className="base-text text-lightgray">{t("mqa3")}</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-5">
            <div>
              <h4 className="regular-text">{t("mq4")}</h4>
              <p className="base-text text-lightgray">{t("mqa4")}</p>
            </div>
          </div>
          <h6 className="underline mt-5 regular-text cursor-pointer" onClick={toggleHide}>{t("hide")}</h6>
        </div>
        <CreateSubjectmodel
          isCreatemodel={isCreatemodel}
          setIsCreatemodel={setIsCreatemodel}
          fetchDecks={fetchDecks}
          addTask={() => { }}
        />
        <WhoSee />
        <MaterialDeckDetailModel data={selectedDeckData} />
      </div>
    </DndProvider>
  );
}
